import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  isBackgroundSyncAvailable,
  isPushNotificationSupported,
  isServiceWorkerSupported,
} from "../utils/browser";

interface PWAStatus {
  /** Indicates if Service Workers are supported by the browser */
  isSWSupported?: boolean;

  /** Indicates if PWA features (Service Worker + Background Sync) are supported */
  isPWASupported?: boolean;

  /** Indicates if the application is online */
  isOnline?: boolean;

  /** Indicates if push notifications are enabled */
  notificationsEnabled?: boolean;

  /** Error message, if any */
  error?: string;
}

interface PWAContextType {
  status: PWAStatus;
  registerForNotifications: () => Promise<NotificationPermission>;
  checkSupport: () => { isSupported: boolean; isPWASupported: boolean };
  clearError: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: ReactNode;
}

export function PWAProvider({ children }: PWAProviderProps) {
  const [status, setStatus] = useState<PWAStatus>({
    isSWSupported: undefined,
    isPWASupported: undefined,
    isOnline: navigator.onLine,
    notificationsEnabled: false,
  });

  const checkSupport = useCallback(() => {
    const isSWSupported = isServiceWorkerSupported();
    const isPWASupported = isSWSupported && isBackgroundSyncAvailable();

    setStatus((prev) => ({
      ...prev,
      isSWSupported: isSWSupported,
      isPWASupported,
    }));

    return { isSupported: isSWSupported, isPWASupported };
  }, []);

  // ✅ Test connettività reale
  const testConnectivity = useCallback(async () => {
    try {
      // Prova a fare una richiesta rapida con timeout via AbortController
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch("/manifest.json", {
        method: "HEAD",
        cache: "no-cache",
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const isReallyOnline = response.ok;
      console.log(
        `🔍 Connectivity test: ${isReallyOnline ? "CONNECTED" : "DISCONNECTED"}`
      );

      setStatus((prev) => ({
        ...prev,
        isOnline: isReallyOnline && navigator.onLine,
      }));

      return isReallyOnline;
    } catch (error) {
      console.warn("🔍 Connectivity test failed:", error);
      setStatus((prev) => ({
        ...prev,
        isOnline: false,
      }));
      return false;
    }
  }, []);

  const registerForNotifications =
    useCallback(async (): Promise<NotificationPermission> => {
      if (!isPushNotificationSupported()) {
        return "denied";
      }

      const permission = await Notification.requestPermission();

      setStatus((prev) => ({
        ...prev,
        notificationsEnabled: permission === "granted",
      }));

      return permission;
    }, []);

  const clearError = useCallback(() => {
    setStatus((prev) => ({ ...prev, error: undefined }));
  }, []);

  // Monitor online/offline status con verifica aggiuntiva
  useEffect(() => {
    const updateOnlineStatus = async () => {
      const navigatorOnline = navigator.onLine;
      console.log(`🌐 Navigator.onLine: ${navigatorOnline}`);

      if (navigatorOnline) {
        // Se navigator dice online, verifica connettività reale
        await testConnectivity();
      } else {
        // Se navigator dice offline, fidati
        setStatus((prev) => ({ ...prev, isOnline: false }));
      }
    };

    const handleOnline = () => {
      console.log("🟢 Browser reports: ONLINE");
      updateOnlineStatus();
    };

    const handleOffline = () => {
      console.log("🔴 Browser reports: OFFLINE");
      setStatus((prev) => ({ ...prev, isOnline: false }));
    };

    // Set initial status
    updateOnlineStatus();

    // Listen for network events
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Additional check with a small delay to catch false positives
    const timeoutId = setTimeout(() => {
      updateOnlineStatus();
    }, 1000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearTimeout(timeoutId);
    };
  }, [testConnectivity]);

  // Initialize on mount
  useEffect(() => {
    checkSupport();
  }, []);

  useEffect(() => {
    Notification.requestPermission();
  }, []);

  const contextValue: PWAContextType = {
    status,
    registerForNotifications,
    checkSupport,
    clearError,
  };

  return (
    <PWAContext.Provider value={contextValue}>{children}</PWAContext.Provider>
  );
}

export function usePWA() {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
}
